import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle,
  Lightning,
  Palette,
  Eraser,
  FileSvg,
  Ruler,
  Package,
  Gauge,
  SignIn,
  UserPlus,
  FlagCheckered,
  Warning,
  CaretDown,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anonStatus, setAnonStatus] = useState(null);

  // Rotating hero headline — switches every 4.5s.
  // Two variants positioned for the dual audience:
  //   1. "Turn Any Logo Into a Decal." — beginner-friendly, business-leaning
  //   2. "Your PNG on the Grid Today." — original, racing-game-leaning
  const HEADLINES = [
    {
      pre: "Turn Any Logo",
      midBreak: true,
      tail: "Into a ",
      accent: "Decal",
    },
    {
      pre: "Your PNG",
      midBreak: true,
      tail: "On the Grid ",
      accent: "Today",
    },
  ];
  const [headlineIdx, setHeadlineIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setHeadlineIdx((i) => (i + 1) % HEADLINES.length);
    }, 4500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (user && user !== false) return;
    api
      .get("/usage/anon")
      .then((r) => setAnonStatus(r.data))
      .catch(() => {});
  }, [user]);

  const tryWithoutAccount = () => {
    navigate("/convert?anon=1");
  };

  return (
    <div>
      {/* HERO — auth gate is now the focal point */}
      <section className="relative border-b border-[var(--border)] overflow-hidden">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center relative">
          <div className="reveal">
            <div className="chip mb-6">
              <span className="status-dot ok"></span>
              LIVE · v2.0 · SECURE CHECKOUT
            </div>
            <h1
              className="font-display font-black tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl"
              data-testid="hero-title"
            >
              {/* Rotating headline — fades between two variants */}
              <span
                key={headlineIdx}
                className="block hero-fade"
                data-testid={`hero-headline-${headlineIdx}`}
              >
                {HEADLINES[headlineIdx].pre}
                {HEADLINES[headlineIdx].midBreak && <br />}
                {HEADLINES[headlineIdx].tail}
                <span className="text-[var(--accent)]">
                  {HEADLINES[headlineIdx].accent}
                </span>
              </span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-[var(--fg-2)] max-w-xl leading-relaxed">
              Upload your image and convert it into a clean, optimised SVG
              decal — ready for racing-game livery editors or any business
              needing a crisp vector. Fast, simple, and built to meet strict
              upload limits automatically.
            </p>

            {/* Auth Gate — the primary decision */}
            <div className="mt-10 hairline bg-white p-6 md:p-7 reveal reveal-delay-1" data-testid="auth-gate">
              <div className="micro-label mb-4 flex items-center gap-2">
                <FlagCheckered size={12} weight="bold" /> Pick your entry point
              </div>
              {user && user !== false ? (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display font-bold">
                    Welcome back, {user.name || user.email.split("@")[0]}.
                  </span>
                  <Link to="/convert" className="btn-accent inline-flex items-center gap-2" data-testid="gate-open-convert">
                    Start Converting <ArrowRight size={14} weight="bold" />
                  </Link>
                  <Link to="/dashboard" className="btn-ghost">Member dashboard</Link>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Link
                      to="/register"
                      className="btn-accent justify-center"
                      data-testid="gate-register"
                    >
                      <UserPlus size={14} weight="bold" /> Register here
                    </Link>
                    <Link
                      to="/login"
                      className="btn-primary justify-center"
                      data-testid="gate-sign-in"
                    >
                      <SignIn size={14} weight="bold" /> Sign in
                    </Link>
                  </div>
                  {/* Secondary CTA — keeps no-account browsing accessible
                      without competing with the primary register/sign-in
                      decision above. Routes to /convert?anon=1 so the
                      existing anonymous-trial flow handles it. */}
                  <button
                    type="button"
                    onClick={tryWithoutAccount}
                    className="mt-3 w-full btn-ghost justify-center"
                    data-testid="gate-explore"
                  >
                    Explore Decal Labs <ArrowRight size={14} weight="bold" />
                  </button>
                </>
              )}

              {!user && (
                <div className="mt-5 flex items-start gap-2 text-xs text-[var(--fg-2)] leading-relaxed">
                  <Warning size={14} weight="fill" className="text-[var(--warning)] mt-0.5 shrink-0" />
                  <span>
                    <strong>Register</strong> to get{" "}
                    <strong>2 free conversions every month</strong> instantly.
                    No card required. See{" "}
                    <Link
                      to="/pricing"
                      className="font-bold text-[var(--accent)] underline underline-offset-2 hover:no-underline"
                      data-testid="gate-pricing-link"
                    >
                      our pricing
                    </Link>{" "}
                    — packs start at <strong>R15</strong>.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Upload guidelines — friendly summary with advanced specs collapsed */}
          <div className="hairline bg-white p-6 md:p-8 relative reveal reveal-delay-2 shadow-brutal">
            <span className="panel-tab">PANEL · 02</span>
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <Gauge size={18} weight="bold" className="text-[var(--accent)]" />
                <span className="micro-label">UPLOAD GUIDELINES</span>
              </div>
              <span className="chip">v2.0</span>
            </div>
            <ul className="mt-6 space-y-3 text-sm" data-testid="upload-guidelines">
              {[
                "Maximum file size — 15 KB",
                "Optimised for game decal upload systems",
                "Clean vector shapes only",
                "Unsupported effects auto-removed",
                "Built-in optimisation and repair tools included",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5">
                  <CheckCircle
                    size={16}
                    weight="fill"
                    className="text-[var(--success)] flex-shrink-0 mt-0.5"
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <details className="mt-6 border-t border-[var(--border)] pt-5 group" data-testid="advanced-specs-dropdown">
              <summary className="flex items-center justify-between cursor-pointer list-none micro-label">
                <span className="flex items-center gap-2">
                  Advanced SVG specifications
                </span>
                <CaretDown
                  size={14}
                  weight="bold"
                  className="transition-transform group-open:rotate-180"
                />
              </summary>
              <div className="mt-4 divide-grid">
                {[
                  ["MAX_SIZE", "15 KB"],
                  ["MAX_PATHS", "~1000"],
                  ["SUPPORTED", "path, rect, circle, polyline, polygon"],
                  ["REJECTED", "filter, mask, text, image, gradient, script"],
                  ["COLORSPACE", "flat RGB · no alpha"],
                  ["VIEWBOX", "tight · preserveAspectRatio: meet"],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[120px_1fr] text-xs font-mono py-3 first:pt-0">
                    <span className="text-[var(--fg-2)]">{k}</span>
                    <span className="truncate">{v}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        </div>

        <div className="border-t border-[var(--border)] bg-black text-white overflow-hidden">
          <div className="whitespace-nowrap py-3 font-mono text-xs tracking-[0.3em] uppercase animate-[marquee_35s_linear_infinite] flex gap-10">
            {Array(10).fill(0).map((_, i) => (
              <span key={i} className="inline-flex items-center gap-3">
                <span className="h-1 w-1 bg-[var(--accent)] inline-block" />
                RASTER TO VECTOR · 15KB CEILING · AUTO FIT · FLAT RGB · LIVERY EDITOR READY · PAY PER PACK · CREDITS NEVER EXPIRE · SECURE CHECKOUT
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
            <div>
              <div className="micro-label">/ Feature set</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-1">
                Built for game decals.
                <br />
                Polished for businesses.
              </h2>
            </div>
            <Link to="/pricing" className="btn-primary inline-flex items-center gap-2">
              See pricing <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            <Feature
              icon={<Lightning weight="fill" />}
              title="Automatic SVG Optimisation"
              desc="Your file is automatically cleaned and optimised for upload limits."
            />
            <Feature
              icon={<Eraser weight="fill" />}
              title="Smart Background Removal"
              desc="Remove unwanted backgrounds before conversion."
            />
            <Feature
              icon={<FileSvg weight="fill" />}
              title="Vector Repair Tools"
              desc="Fix broken or incompatible SVG files instantly."
            />
            <Feature
              icon={<Ruler weight="fill" />}
              title="File Size Compression"
              desc="Automatically reduce SVG size while preserving quality."
            />
            <Feature
              icon={<Palette weight="fill" />}
              title="Multi-Color Support"
              desc="Maintain clean colours and layers during conversion."
            />
            <Feature
              icon={<Package weight="fill" />}
              title="Decal Ready Downloads"
              desc="Download optimised SVG files ready for upload."
            />
          </div>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="border-b border-[var(--border)] bg-[var(--bg-2)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="micro-label">/ Conversion packs</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">
            Pay per pack. No subscriptions. From R15.
          </h2>
          <p className="text-[var(--fg-2)] mt-3 max-w-xl">
            1 credit = 1 conversion. Paid credits never expire — plus 2 free conversions
            every month on every account.
          </p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)]">
            <MiniPlan name="Starter" price="R15" note="5 conversions · once-off" />
            <MiniPlan name="Standard" price="R35" note="15 conversions · once-off" />
            <MiniPlan name="Pro" price="R69" note="35 conversions · best value" highlight />
            <MiniPlan name="Studio" price="R149" note="100 conversions · once-off" />
          </div>
          <div className="mt-8 text-center">
            <Link to="/pricing" className="btn-accent inline-flex items-center gap-2" data-testid="strip-see-pricing">
              See all packs <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-[var(--border)] bg-black text-white relative">
        <div className="grain absolute inset-0" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <div className="micro-label text-white/60">/ Flow</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-1">
              Four simple steps
              <br />
              from logo to decal.
            </h2>
            <p className="mt-6 text-white/70 max-w-lg">
              No design experience needed. Upload your image, we do the rest —
              and the result is ready to drop straight into your livery editor.
            </p>
          </div>
          <ol className="divide-y divide-white/10 border border-white/10">
            {[
              ["01", "Upload", "Choose your logo or image file."],
              ["02", "Trace", "Convert your image into a clean vector decal."],
              ["03", "Validate", "Automatically check file compatibility and size limits."],
              ["04", "Download", "Save your optimised SVG and upload it to your game."],
            ].map(([n, t, d]) => (
              <li key={n} className="p-6 md:p-8 grid grid-cols-[1fr_1fr] gap-6 items-start group hover:bg-white/[0.03] transition-colors">
                <span className="numeric-massive text-[var(--accent)] select-none">{n}</span>
                <div>
                  <div className="font-display font-bold text-xl md:text-2xl">{t}</div>
                  <div className="text-white/60 mt-2 text-sm md:text-base">{d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <div className="micro-label">/ Ready</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mt-2 max-w-4xl mx-auto">
            Turn your logo into a decal.
          </h2>
          <p className="mt-6 text-[var(--fg-2)] max-w-xl mx-auto">
            2 free conversions every month — no card needed. Conversion packs from R15, and paid credits never expire.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/register" className="btn-mega" data-testid="cta-footer-register">
              Try It Free · 2 conversions <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to="/pricing" className="btn-ghost" data-testid="cta-footer-pricing">
              See pricing
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {["Cancel anytime", "ZAR billing", "POPIA Compliant", "Works on mobile"].map((p) => (
              <span key={p} className="chip">
                <CheckCircle size={12} weight="fill" className="text-[var(--success)]" />
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="bg-white p-8 group hover:bg-black hover:text-white transition-colors duration-200">
      <div className="h-10 w-10 border border-current flex items-center justify-center group-hover:border-[var(--accent)] group-hover:text-[var(--accent)]">
        {icon}
      </div>
      <div className="font-display font-bold text-xl mt-6">{title}</div>
      <div className="text-sm mt-2 opacity-75">{desc}</div>
    </div>
  );
}

function MiniPlan({ name, price, note, highlight }) {
  return (
    <div
      className={`bg-white p-6 md:p-8 relative transition-transform ${
        highlight
          ? "border-2 border-[var(--fg)] shadow-brutal-accent z-10"
          : "hover:-translate-y-0.5"
      }`}
    >
      {highlight && (
        <span className="panel-tab" style={{ background: "var(--accent)" }}>
          MOST PICKED
        </span>
      )}
      <div className="font-display font-bold text-2xl mt-3">{name}</div>
      <div className="font-display font-black text-5xl mt-3 tracking-tight">{price}</div>
      <div className="text-xs font-mono text-[var(--fg-2)] tracking-wider mt-2">{note}</div>
    </div>
  );
}
